import { z } from 'zod';
/**
 * Per `customer-fan-groups` capability. Two entities:
 *
 *   FanGroup — owned by a single customer (B2B operator). Acts as the
 *   container; future changes bind it to character access policies
 *   (`character-access-policy`) and group wallets (`fan-wallet-and-points`).
 *
 *   FanGroupMembership — joins fans to groups. A fan can belong to
 *   many groups across many customers; composite (groupId, fanId) is
 *   the unique key.
 */
export const FanGroupSchema = z.object({
    groupId: z.string().uuid(),
    /** Operator Cognito sub of the group's owner customer. */
    customerId: z.string().min(1),
    name: z.string().min(1).max(80),
    description: z.string().max(500).default(''),
    /** Denormalized counter; authoritative source is the memberships table. */
    memberCount: z.number().int().min(0).default(0),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const FanGroupMembershipSchema = z.object({
    groupId: z.string().uuid(),
    fanId: z.string().min(1),
    joinedAt: z.string().datetime(),
    /**
     * 'customer' — operator added the fan via admin UI / API
     * 'self'     — fan auto-joined (Phase 3 customer-default-group path,
     *              or future invitation flow)
     */
    joinedBy: z.enum(['customer', 'self']),
});
/** Shape returned by GET /api/admin/groups list response. */
export const FanGroupSummarySchema = FanGroupSchema.pick({
    groupId: true,
    customerId: true,
    name: true,
    description: true,
    memberCount: true,
    createdAt: true,
    updatedAt: true,
});
/** Shape of one entry in GET /api/admin/groups/{groupId}/members. */
export const FanGroupMemberRowSchema = z.object({
    fanId: z.string().min(1),
    email: z.string().email().toLowerCase(),
    displayName: z.string().max(80),
    joinedAt: z.string().datetime(),
});
